import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { CampaignCard } from "components/campaign-card";
import { Alert, AlertDescription, AlertTitle } from "components/ui/alert";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";
import { AlertCircle, RotateCw, Search, SlidersHorizontal, XCircle } from "lucide-react";
import api, { isSuccessResponse } from "lib/api";
import {
  buildProjectsListUrl,
  mapProjectToCampaignCard,
  parseProjectsListResponse,
  PROJECT_STATUS_OPTIONS,
} from "lib/projects";
import { cn } from "lib/utils";
import { getIndustryRecordId, selectIndustries, selectIsAuthenticated } from "store/reducer";
import { useDebouncedValue } from "hooks/useDebouncedValue";

const ALL_CATEGORY_VALUE = "all";

const opportunityCampaignShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
});

function OpportunitiesCampaignGrid({ campaigns }) {
  return (
    <div className="mx-auto flex w-full max-w-6xl flex-wrap items-stretch justify-center gap-6">
      {campaigns.map((campaign) => (
        <div
          key={campaign.id}
          className="w-full min-w-0 max-w-md sm:max-w-88 lg:max-w-96"
        >
          <CampaignCard campaign={campaign} className="h-full" />
        </div>
      ))}
    </div>
  );
}

function OpportunitiesListContent({
  listLoading,
  listError,
  campaigns,
  listTotal,
  hasToolbarReset,
  onRefresh,
  onResetFilters,
}) {
  if (listLoading && campaigns.length === 0) {
    return (
      <p className="text-center text-sm text-muted-foreground">Loading offerings…</p>
    );
  }

  if (listError && campaigns.length === 0 && !listLoading) {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <p className="text-muted-foreground text-sm">
          Unable to load offerings. Check your connection or use Try again in the
          message above.
        </p>
        <Button
          type="button"
          variant="outline"
          className="mt-4"
          disabled={listLoading}
          onClick={onRefresh}
        >
          Try again
        </Button>
      </div>
    );
  }

  if (campaigns.length > 0) {
    return (
      <>
        <p className="mb-6 text-sm text-muted-foreground">
          Showing {campaigns.length} offering{campaigns.length === 1 ? "" : "s"}
          {listTotal > campaigns.length ? ` of ${listTotal}` : ""}
        </p>
        <OpportunitiesCampaignGrid campaigns={campaigns} />
      </>
    );
  }

  if (!hasToolbarReset) {
    return (
      <p className="py-16 text-center text-sm text-muted-foreground">
        No offerings available yet.
      </p>
    );
  }

  return (
    <div className="py-16 text-center">
      <p className="text-lg text-muted-foreground">
        No offerings found matching your criteria.
      </p>
      <Button variant="outline" className="mt-4" onClick={onResetFilters}>
        Clear filters
      </Button>
    </div>
  );
}

OpportunitiesCampaignGrid.propTypes = {
  campaigns: PropTypes.arrayOf(opportunityCampaignShape).isRequired,
};

OpportunitiesListContent.propTypes = {
  listLoading: PropTypes.bool.isRequired,
  listError: PropTypes.string,
  campaigns: PropTypes.arrayOf(opportunityCampaignShape).isRequired,
  listTotal: PropTypes.number.isRequired,
  hasToolbarReset: PropTypes.bool.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
};

export default function OpportunitiesPage() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const industries = useSelector(selectIndustries);

  const [campaigns, setCampaigns] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const [searchDraft, setSearchDraft] = useState("");
  const { debounced: debouncedSearch, flush: flushSearch } = useDebouncedValue(searchDraft, 400);

  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORY_VALUE);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");

  const listQuery = useMemo(() => {
    const q = {};
    const search = debouncedSearch.trim();
    if (search) q.search = search;
    if (selectedCategory !== ALL_CATEGORY_VALUE) q.industry = selectedCategory;
    if (statusFilter !== "all") q.status = statusFilter;
    if (sortBy !== "recent") q.sort = sortBy;
    return q;
  }, [debouncedSearch, selectedCategory, statusFilter, sortBy]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setListLoading(true);
      setListError(null);
      try {
        const path = buildProjectsListUrl(listQuery);
        const resp = await api.get(path, { auth: false });
        if (cancelled) return;
        if (!isSuccessResponse(resp)) {
          setCampaigns([]);
          setListTotal(0);
          setListError(resp?.message || "Could not load projects");
          return;
        }
        const { items, total } = parseProjectsListResponse(resp?.data ?? resp);
        const mapped = items
          .map((p) => mapProjectToCampaignCard(p, { descriptionMaxLen: 50 }))
          .filter(Boolean);
        setCampaigns(mapped);
        setListTotal(Number.isFinite(total) ? total : mapped.length);
        setListError(null);
      } catch (e) {
        if (cancelled) return;
        setCampaigns([]);
        setListTotal(0);
        setListError(e?.message || "Could not load projects");
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [listQuery, refreshNonce]);

  useEffect(() => {
    if (!isAuthenticated) {
      setSelectedCategory(ALL_CATEGORY_VALUE);
    }
  }, [isAuthenticated]);

  const hasToolbarReset =
    Boolean(searchDraft.trim()) ||
    selectedCategory !== ALL_CATEGORY_VALUE ||
    statusFilter !== "all" ||
    sortBy !== "recent";

  const resetFilters = () => {
    setSearchDraft("");
    flushSearch();
    setSelectedCategory(ALL_CATEGORY_VALUE);
    setStatusFilter("all");
    setSortBy("recent");
  };

  const handleRefresh = () => {
    setRefreshNonce((n) => n + 1);
  };

  return (
    <>
      <section className="bg-muted/30 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Investment Offerings</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Browse all available equity crowdfunding opportunities. Invest in private companies from various sectors.
          </p>
        </div>
      </section>

      <section className="sticky top-16 z-40 border-b bg-background py-6">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex w-full flex-1 flex-col gap-4 sm:flex-row sm:items-center">
              <div className="relative min-w-0 flex-1 sm:max-w-md">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") flushSearch();
                  }}
                  placeholder="Search offerings..."
                  className="pl-10"
                  aria-label="Search offerings"
                />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full shrink-0 sm:w-[200px]" aria-label="Industry">
                  <SelectValue placeholder="Industry" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_CATEGORY_VALUE}>All</SelectItem>
                  {industries.length > 0
                    ? industries.map((ind) => {
                        const id = getIndustryRecordId(ind);
                        if (!id) return null;
                        const label = ind.name ?? ind.title ?? ind.label ?? ind.slug ?? id;
                        return (
                          <SelectItem key={id} value={id}>
                            {label}
                          </SelectItem>
                        );
                      })
                    : null}
                </SelectContent>
              </Select>
            </div>

            <div className="flex w-full flex-wrap items-center gap-3 lg:w-auto lg:justify-end">
              <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full sm:w-auto">
                <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-3 lg:inline-flex lg:w-auto">
                  <TabsTrigger value="all" className="px-3">
                    All
                  </TabsTrigger>
                  {PROJECT_STATUS_OPTIONS.map((opt) => (
                    <TabsTrigger key={opt.value} value={opt.value} className="px-3">
                      {opt.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>

              <div className="flex flex-1 flex-wrap items-center justify-end gap-2 sm:flex-initial">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="min-w-0 flex-1 sm:w-[180px] sm:flex-initial" aria-label="Sort by">
                    <SlidersHorizontal className="mr-2 h-4 w-4 shrink-0" />
                    <SelectValue placeholder="Sort by" className="truncate" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recent">Most recent</SelectItem>
                    {/* <SelectItem value="raised">Most raised</SelectItem>
                    <SelectItem value="popular">Most popular</SelectItem> */}
                    <SelectItem value="ending">Ending soon</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Refresh listings"
                  disabled={listLoading}
                  onClick={handleRefresh}
                  className="shrink-0"
                >
                  <RotateCw className={cn("h-4 w-4", listLoading && "animate-spin")} />
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Clear filters"
                  disabled={!hasToolbarReset}
                  onClick={resetFilters}
                  className="shrink-0"
                  title={hasToolbarReset ? "Clear filters" : "No filters to clear"}
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {listError ? (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle className="col-start-2">Could not load offerings</AlertTitle>
              <AlertDescription className="col-start-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-destructive/90">{listError}</span>
                <Button type="button" variant="outline" size="sm" disabled={listLoading} onClick={handleRefresh}>
                  Try again
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <OpportunitiesListContent
            listLoading={listLoading}
            listError={listError}
            campaigns={campaigns}
            listTotal={listTotal}
            hasToolbarReset={hasToolbarReset}
            onRefresh={handleRefresh}
            onResetFilters={resetFilters}
          />
        </div>
      </section>
    </>
  );
}
